import React, { createContext, useContext, useState, useCallback } from 'react';
import apiClient from '../utils/api';

// Active Listing Context - Stores scraped property data globally
const ActiveListingContext = createContext(null);

// High-end placeholder image for when no property photos are available
const PLACEHOLDER_IMAGE = 'https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=1200&q=80';

export const ActiveListingProvider = ({ children }) => {
  // Core property data from scraping
  const [activeListing, setActiveListing] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Brand Kit data
  const [brandKit, setBrandKit] = useState(null);
  
  // Track which formats have been created
  const [createdFormats, setCreatedFormats] = useState({
    post: false,
    story: false,
    reel: false,
    flyer: false,
    postcard: false,
    brochure: false
  });

  // RealProp Magic Import: Scrape a listing URL
  const magicCreate = useCallback(async (listingUrl) => {
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await apiClient.post('/magic-create/scrape', {
        listing_url: listingUrl
      });
      
      if (response.data.success) {
        // Store listing URL along with scraped data
        const scrapedData = response.data.data;
        
        // Ensure photos array exists and has fallback
        const photos = scrapedData.photos && scrapedData.photos.length > 0 
          ? scrapedData.photos 
          : [PLACEHOLDER_IMAGE];
        
        // Set main image with fallback
        const mainImageUrl = scrapedData.main_image_url || photos[0];
        
        const dataWithUrl = {
          ...scrapedData,
          listing_url: listingUrl,
          photos: photos,
          main_image_url: mainImageUrl,
          has_real_photos: scrapedData.photos && scrapedData.photos.length > 0
        };
        
        setActiveListing(dataWithUrl);
        // Reset created formats when new listing is loaded
        setCreatedFormats({
          post: false,
          story: false,
          reel: false,
          flyer: false,
          postcard: false,
          brochure: false
        });
        return { success: true, data: dataWithUrl };
      } else {
        setError(response.data.error);
        return { success: false, error: response.data.error };
      }
    } catch (err) {
      const errorMsg = err.response?.data?.detail || 'Failed to scrape listing';
      setError(errorMsg);
      return { success: false, error: errorMsg };
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Load Brand Kit
  const loadBrandKit = useCallback(async () => {
    try {
      const response = await apiClient.get('/profile/brand-kit');
      setBrandKit(response.data);
      return response.data;
    } catch (err) {
      console.error('Failed to load brand kit:', err);
      return null;
    }
  }, []);

  // Regenerate description in different styles
  const regenerateDescription = useCallback(async (style) => {
    if (!activeListing) return { success: false, error: 'No active listing' };
    
    try {
      const response = await apiClient.post('/ai/regenerate-description', {
        property_data: activeListing,
        style: style
      });
      
      if (response.data.success) {
        // Update active listing with new description
        setActiveListing(prev => ({
          ...prev,
          property_summary: response.data.description
        }));
        return { success: true, description: response.data.description };
      } else {
        return { success: false, error: response.data.error };
      }
    } catch (err) {
      if (err.response?.status === 403) {
        const detail = err.response?.data?.detail;
        return { success: false, error: detail?.reason || 'This feature requires a plan upgrade.', upgrade_to: detail?.upgrade_to };
      }
      return { success: false, error: 'Failed to regenerate description' };
    }
  }, [activeListing]);

  // Generate QR code for listing URL
  const generateQRCode = useCallback(async (url, size = 200) => {
    try {
      const response = await apiClient.post('/ai/generate-qr', {
        url: url,
        size: size
      });
      
      if (response.data.success) {
        return { success: true, qr_code: response.data.qr_code };
      } else {
        return { success: false, error: response.data.error };
      }
    } catch (err) {
      return { success: false, error: 'Failed to generate QR code' };
    }
  }, []);

  // Fetch listing from Supabase by ID
  const fetchListing = useCallback(async (listingId) => {
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await apiClient.get(`/listings/${listingId}`);
      const listingData = response.data;
      
      // Ensure photos array exists and has fallback
      const photos = listingData.photos && listingData.photos.length > 0 
        ? listingData.photos 
        : [PLACEHOLDER_IMAGE];
      
      const mainImageUrl = listingData.main_image_url || photos[0];
      
      const dataWithPhotos = {
        ...listingData,
        photos: photos,
        main_image_url: mainImageUrl,
        has_real_photos: listingData.photos && listingData.photos.length > 0
      };
      
      setActiveListing(dataWithPhotos);
      setCreatedFormats({
        post: false,
        story: false,
        reel: false,
        flyer: false,
        postcard: false,
        brochure: false
      });
      
      return { success: true, data: dataWithPhotos };
    } catch (err) {
      const errorMsg = err.response?.data?.detail || 'Failed to fetch listing';
      setError(errorMsg);
      return { success: false, error: errorMsg };
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Get user's saved listings from Supabase
  const fetchUserListings = useCallback(async () => {
    try {
      const response = await apiClient.get('/listings');
      return { success: true, listings: response.data.listings || [] };
    } catch (err) {
      return { success: false, error: 'Failed to fetch listings', listings: [] };
    }
  }, []);

  // Get main image URL with fallback
  const getMainImageUrl = useCallback(() => {
    if (!activeListing) return PLACEHOLDER_IMAGE;
    return activeListing.main_image_url || 
           (activeListing.photos && activeListing.photos[0]) || 
           PLACEHOLDER_IMAGE;
  }, [activeListing]);

  // Get photo at specific index with fallback
  const getPhotoAtIndex = useCallback((index) => {
    if (!activeListing || !activeListing.photos) return PLACEHOLDER_IMAGE;
    return activeListing.photos[index] || PLACEHOLDER_IMAGE;
  }, [activeListing]);

  // Check if listing has real scraped photos
  const hasRealPhotos = useCallback(() => {
    return activeListing?.has_real_photos || false;
  }, [activeListing]);

  // Set manual listing data (for when user enters data manually)
  const setManualListing = useCallback((data) => {
    setActiveListing(data);
    setError(null);
  }, []);

  // Refresh a saved listing by re-scraping its source URL. Picks up precise
  // street/city/state/zip + fresh price/beds/photos while preserving the
  // user's edited summary & key features.
  const refreshListing = useCallback(async () => {
    const listingId = activeListing?.listing_id || activeListing?.id;
    const listingUrl = activeListing?.listing_url;
    if (!listingUrl) {
      return { success: false, error: 'This listing has no source URL to refresh from.' };
    }
    setIsLoading(true);
    try {
      // Saved listing → server-side refresh endpoint. Otherwise re-scrape.
      if (listingId) {
        const response = await apiClient.post(`/listings/${listingId}/refresh`);
        if (response.data?.success) {
          const fresh = response.data.listing;
          const photos = fresh.photos && fresh.photos.length > 0 ? fresh.photos : (activeListing.photos || []);
          const merged = {
            ...activeListing,
            ...fresh,
            photos,
            main_image_url: photos[0] || activeListing.main_image_url,
            has_real_photos: photos.length > 0,
          };
          setActiveListing(merged);
          return { success: true, data: merged };
        }
        return { success: false, error: 'Could not refresh listing' };
      }
      const result = await magicCreate(listingUrl);
      return result;
    } catch (err) {
      const errorMsg = err.response?.data?.detail || 'Failed to refresh listing';
      if (typeof errorMsg === 'object' && errorMsg.reason) {
        return { success: false, error: errorMsg.reason, upgrade_to: errorMsg.upgrade_to };
      }
      return { success: false, error: typeof errorMsg === 'string' ? errorMsg : 'Failed to refresh listing' };
    } finally {
      setIsLoading(false);
    }
  }, [activeListing, magicCreate]);

  // Clear active listing
  const clearListing = useCallback(() => {
    setActiveListing(null);
    setError(null);
    setCreatedFormats({
      post: false,
      story: false,
      reel: false,
      flyer: false,
      postcard: false,
      brochure: false
    });
  }, []);

  // Save photo adjustments to listing in Supabase
  const savePhotoAdjustments = useCallback(async (adjustments) => {
    const listingId = activeListing?.listing_id || activeListing?.id;
    if (!listingId || !adjustments) return;
    try {
      await apiClient.put(`/listings/${listingId}`, { photo_adjustments: adjustments });
    } catch (err) {
      console.error('Failed to save photo adjustments:', err);
    }
  }, [activeListing]);

  // Mark a format as created
  const markFormatCreated = useCallback((format) => {
    setCreatedFormats(prev => ({
      ...prev,
      [format]: true
    }));
  }, []);

  // Get formatted price
  const getFormattedPrice = useCallback(() => {
    if (!activeListing?.price) return '';
    const price = activeListing.price.toString().replace(/[^0-9]/g, '');
    return `$${parseInt(price).toLocaleString()}`;
  }, [activeListing]);

  // Get formatted stats (beds/baths/sqft)
  const getFormattedStats = useCallback(() => {
    if (!activeListing) return '';
    const { beds, baths, sqft } = activeListing;
    const parts = [];
    if (beds) parts.push(`${beds} Beds`);
    if (baths) parts.push(`${baths} Baths`);
    if (sqft) parts.push(`${sqft.toString().replace(/[^0-9]/g, '')} Sq Ft`);
    return parts.join(' • ');
  }, [activeListing]);

  // Intelligent Mapper: Map listing data to template elements
  const mapDataToElements = useCallback((elements, options = {}) => {
    if (!activeListing) return elements;
    
    const { applyBrandKit = true } = options;
    
    return elements.map(element => {
      let updatedElement = { ...element };
      
      // Map by element ID patterns
      const id = element.id?.toLowerCase() || '';
      const text = element.text || '';
      
      // Price mapping
      if (id.includes('price') || text.includes('{{price}}') || text.includes('${{price}}')) {
        updatedElement.text = text
          .replace(/\$?\{\{price\}\}/g, getFormattedPrice())
          .replace(/\$[0-9,]+/g, getFormattedPrice());
        if (id.includes('price') && !text.includes('{{')) {
          updatedElement.text = getFormattedPrice();
        }
      }
      
      // Address mapping
      if (id.includes('address') || text.includes('{{address}}')) {
        updatedElement.text = text.replace(/\{\{address\}\}/g, activeListing.address || '');
        if (id.includes('address') && !text.includes('{{')) {
          updatedElement.text = activeListing.address || '';
        }
      }
      
      // Beds mapping
      if (text.includes('{{beds}}')) {
        updatedElement.text = text.replace(/\{\{beds\}\}/g, activeListing.beds || '');
      }
      
      // Baths mapping
      if (text.includes('{{baths}}')) {
        updatedElement.text = text.replace(/\{\{baths\}\}/g, activeListing.baths || '');
      }
      
      // Sqft mapping
      if (text.includes('{{sqft}}')) {
        const sqft = activeListing.sqft?.toString().replace(/[^0-9]/g, '') || '';
        updatedElement.text = text.replace(/\{\{sqft\}\}/g, sqft);
      }
      
      // Stats/Summary mapping
      if (id.includes('stats') || id.includes('features') || text.includes('{{stats}}')) {
        updatedElement.text = text.replace(/\{\{stats\}\}/g, getFormattedStats());
        if ((id.includes('stats') || id.includes('features')) && !text.includes('{{')) {
          updatedElement.text = getFormattedStats();
        }
      }
      
      // Property summary mapping
      if (id.includes('summary') || id.includes('description') || text.includes('{{summary}}')) {
        updatedElement.text = text.replace(/\{\{summary\}\}/g, activeListing.property_summary || '');
      }
      
      // Apply Brand Kit colors and fonts if enabled
      if (applyBrandKit && brandKit) {
        // Apply primary color to headers/titles
        if (id.includes('header') || id.includes('title') || id.includes('price')) {
          if (brandKit.primary_color) {
            updatedElement.fill = brandKit.primary_color;
          }
        }
        
        // Apply accent color to special elements
        if (id.includes('accent') || id.includes('highlight')) {
          if (brandKit.accent_color) {
            updatedElement.fill = brandKit.accent_color;
          }
        }
        
        // Apply fonts
        if (element.type === 'text') {
          if (id.includes('header') || id.includes('title') || id.includes('price')) {
            if (brandKit.font_primary) {
              updatedElement.fontFamily = brandKit.font_primary;
            }
          } else {
            if (brandKit.font_secondary) {
              updatedElement.fontFamily = brandKit.font_secondary;
            }
          }
        }
      }
      
      return updatedElement;
    });
  }, [activeListing, brandKit, getFormattedPrice, getFormattedStats]);

  // Get print-ready settings (300 DPI with bleeds)
  const getPrintSettings = useCallback((format) => {
    const settings = {
      flyer: {
        width: 2550,  // 8.5" × 300 DPI
        height: 3300, // 11" × 300 DPI
        bleed: 36,    // 3mm ≈ 0.12" × 300 DPI
        dpi: 300
      },
      postcard: {
        width: 1200,  // 4" × 300 DPI
        height: 1800, // 6" × 300 DPI
        bleed: 36,
        dpi: 300
      },
      brochure: {
        width: 3300,  // 11" × 300 DPI
        height: 2550, // 8.5" × 300 DPI
        bleed: 36,
        dpi: 300
      }
    };
    return settings[format] || settings.flyer;
  }, []);

  const value = {
    // State
    activeListing,
    isLoading,
    error,
    brandKit,
    createdFormats,
    
    // Actions
    magicCreate,
    loadBrandKit,
    setManualListing,
    setActiveListing,
    clearListing,
    markFormatCreated,
    regenerateDescription,
    generateQRCode,
    fetchListing,
    fetchUserListings,
    refreshListing,
    savePhotoAdjustments,
    
    // Image helpers
    getMainImageUrl,
    getPhotoAtIndex,
    hasRealPhotos,
    PLACEHOLDER_IMAGE,
    
    // Helpers
    getFormattedPrice,
    getFormattedStats,
    mapDataToElements,
    getPrintSettings,
    
    // Convenience check
    hasActiveListing: !!activeListing
  };

  return (
    <ActiveListingContext.Provider value={value}>
      {children}
    </ActiveListingContext.Provider>
  );
};

export const useActiveListing = () => {
  const context = useContext(ActiveListingContext);
  if (!context) {
    throw new Error('useActiveListing must be used within an ActiveListingProvider');
  }
  return context;
};

export default ActiveListingContext;
